import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import { useLazyQuery } from "@apollo/client";
import { GET_SENT_EMAIL_BY_ID } from "../../../../utils/GraphQL/queries";
/* The EmailModal sheet, imported EXPLICITLY. This modal had its own
   stylesheet that was a 1:1 twin of that one — same nine recipes under
   `sent-` prefixes, and still carrying the pre-token literals the other
   lost on 2026-10-06. Two copies of one design is what the row
   extraction was about; this is the same argument. */
import "../EmailModal/emailModal.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import copyImageToClip from "../../../../utils/copyImageToClip";
import { baImageUrl } from "../../../../utils/baImageUrl";

const SentEmailModal = ({ sentId, closeEmail }) => {
  const { handleSnack } = useSnackbar();
  const [dateFormat, setDateFormat] = useState("");
  const [email, setEmail] = useState({});
  const [emailLoading, setEmailLoading] = useState(true);
  const htmlRef = useRef();
  const [loadEmail] = useLazyQuery(GET_SENT_EMAIL_BY_ID);

  useEffect(() => {
    emailFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentId]);

  useEffect(() => {
    let date = "";
    if (htmlRef.current) {
      htmlRef.current.innerHTML = DOMPurify.sanitize(
        email?.emailHTML ||
        '<h5 style="text-align: center; width: 100%;">No Message Body</h5>'
      );
    }
    const getNum = parseInt(email?.dateCreated);
    if (getNum > 0) {
      date = BAGroupTimeFormat(getNum, true);
    }
    setDateFormat(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // PDFs render straight from the signed `s3FileUrl` (see the iframe + iOS
  // button below). The base64 `fileData` → blob: path was removed 2026-09-15:
  // every PdfFile is on S3 (195/195 in bdr_prod, none with a `fileData`
  // column), so the fallback only ever ran by making the server download the
  // PDF from S3 and base64-encode it on every open — for bytes this modal
  // then ignored in favour of the URL. Mirrors the inbox `EmailModal`.

  const emailFetch = async () => {
    const { data } = await loadEmail({
      variables: {
        sentId: sentId,
      },
    });

    if (data?.getSentEmailById) {
      setEmail({ ...data?.getSentEmailById });
    }

    setEmailLoading(false);
  };

  /*
   * `handleSnack` comes from the global snackbar context. This file used to
   * own a MUI <Snackbar> plus its own open/close state and a `blankSnack`
   * literal — a second snackbar that could sit on top of the app's real one.
   */

  return (
    <>
      {emailLoading && <AdminBDRSpinner message={"Loading..."} />}
      <BdrModal
        open
        onClose={closeEmail}
        size="lg"
        variant="panel"
        title="Sent Email"
        actions={<BdrButton onClick={closeEmail}>Close</BdrButton>}
      >
          <ul className="email-modal-list">
            <li>
              <h5>
                <span className="green-color-text">To:</span> {email?.to}
              </h5>

              <h5>
                <span className="green-color-text">Subject:</span>{" "}
                {email?.emailSubject}
              </h5>
              <span>
                <span className="green-color-text">Date:</span> {dateFormat}
              </span>
              <span>
                <span className="green-color-text">From:</span> {email?.from}
              </span>
            </li>
            <li ref={htmlRef} className="email-modal-body-html"></li>
          </ul>
          {/*
            The guard was INSIDE this div, so an email with no attachments
            still rendered the container — and the container has a border, a
            fill and a shadow, so it showed up as an empty box on every single
            message. Guarding the element itself is the fix.
          */}
          {(email?.attachImgs?.length > 0 ||
            email?.attachPdf?.length > 0) && (
            <div className="email-modal-images-container">
              <h3 className="email-modal-img-hdr">Attachments</h3>
                {email?.attachImgs?.length > 0 &&
                  email?.attachImgs?.map((item, index) => {
                    const imgId = "attImgEl" + item._id;
                    const btnId = "attBtnEl" + item._id;

                    const html = (
                      <div
                        key={item._id + index}
                        className="email-modal-images-box"
                      >
                        <div className="email-modal-img-hdr-box">
                          <h5 className="email-modal-tile-label">
                            Attached Image {index + 1}
                          </h5>
                          <BdrButton
                            size="sm"
                            variant="action"
                            id={btnId}
                            onPointerUp={async () => {
                              const img = document.querySelector(`#${imgId}`);
                              const blob = new ClipboardItem({
                                "image/png": copyImageToClip(img.src),
                              });

                              navigator.clipboard
                                .write([blob])
                                .then(() => handleSnack("Image copied", 1500))
                                .catch((err) => {
                                  console.error(
                                    "[SentEmailModal] copy image failed",
                                    { src: img.src, err }
                                  );
                                  handleSnack("Couldn't copy the image", 2500);
                                });
                            }}
                            icon={<ContentCopyIcon fontSize="small" />}
                          >
                            Copy
                          </BdrButton>
                        </div>

                        <img
                          id={imgId}
                          className="email-modal-img"
                          src={item?.s3ImageUrl ?? baImageUrl(item?._id)}
                          alt={`door pic ${index}`}
                        />
                      </div>
                    );

                    return html;
                  })}
                {email?.attachPdf?.length > 0 &&
                  email?.attachPdf?.map((item, index) => {
                    const html = (
                      <div
                        key={item?.id + index}
                        className="email-modal-pdf-box"
                      >
                        {/iP(hone|od|ad)/.test(navigator.userAgent) ? (
                          <div>
                            <BdrButton
                              size="sm"
                              variant="action"
                              onClick={() => {
                                // Signed S3 URL is the only source (base64
                                // `fileData` no longer queried). The old
                                // fallback ran `item?.fileData.split` and
                                // threw when the field was absent.
                                if (item?.s3FileUrl) {
                                  window.open(item.s3FileUrl);
                                  return;
                                }
                                handleSnack(
                                  "PDF unavailable — this attachment has no S3 copy",
                                  3000
                                );
                              }}
                            >
                              View PDF {index + 1}{" "}
                              {item?.fileName && `- ${item?.fileName}`}
                            </BdrButton>
                          </div>
                        ) : (
                          <>
                            <div className="email-modal-img-hdr-box">
                              <h5 className="email-modal-tile-label">
                                Attached Pdf {index + 1}
                              </h5>
                              <span style={{ fontSize: "smaller" }}>
                                File Name: {item?.fileName}
                              </span>
                            </div>
                            <div className="email-modal-pdf-file">
                              <iframe
                                src={
                                  item?.s3FileUrl
                                    ? `${item.s3FileUrl}#zoom=50`
                                    : undefined
                                }
                                style={{ width: "300px", height: "370px" }}
                                title={`PDF ${index + 1}`}
                              ></iframe>
                            </div>
                          </>
                        )}
                      </div>
                    );

                    return html;
                  })}
            </div>
          )}
      </BdrModal>
    </>
  );
};

export default SentEmailModal;
